import { DEFAULT_TITLE_COLOR, designFontStack, readableTextOn, TEXT_ON_DARK } from "./design"

// §75: each sheet style's chart chrome (V2 only): the baseline, the grid lines, the labels and the legend, in the
// style's own ink, weight and line; the data keeps the Brand palette and the layout is untouched. Applied to the option
// chartOption built (ChartWidget), so his settings (grid off, labels off, rotation) still decide what shows.
//   Swiss: an ink baseline, hairline grid lines, quiet labels, the legend as short dashes (as the rules are).
//   Cards: a soft grey baseline, dotted grid lines, the legend's round swatches.
//   Bold:  a 2px baseline in the header colour, firm labels, a heavier legend with square swatches.
//   Lesha: the bank's own factsheet: no grid, a grey baseline, its navy / blue / grey palette, pill bars on light tracks.
export const SHEET_CHART_CHROME = {
  swiss: { baseline: "rule", baselineWidth: 1, grid: "#eaecf0", gridType: "solid", label: "#667085", labelWeight: 400, legend: "#344054", legendWeight: 400, icon: "rect", iconSize: [12, 3] },
  cards: { baseline: "#d0d5dd", baselineWidth: 1, grid: "#e4e7ec", gridType: [2, 3], label: "#667085", labelWeight: 400, legend: "#475467", legendWeight: 500, icon: "roundRect", iconSize: [14, 8] },
  bold: { baseline: "rule", baselineWidth: 2, grid: "#e4e7ec", gridType: "solid", label: "#344054", labelWeight: 500, legend: "#101828", legendWeight: 600, icon: "rect", iconSize: [12, 12] },
  // The bank's own factsheet (Artyom's references: «стили графиков»): faint grid lines on a line chart, none behind
  // bars (their tracks are the scale), a navy baseline, navy axis labels (the categories bold), the legend under the
  // plot with square swatches and each name in its series' colour, grey value labels, all of it small (10px: «текст
  // мелкий довольно на графиках») on an airy grid; its palette and pill bars below.
  lesha: { baseline: "#163a63", baselineWidth: 1, grid: "#ececec", gridType: "solid", label: "#163a63", labelWeight: 400, categoryWeight: 600, legend: "#163a63", legendWeight: 500, icon: "rect", iconSize: [8, 8], fontSize: 10 },
}

// §75 Lesha: the colours of Lesha Bank's own factsheet (navy, its blue, a warm grey; the light track behind each bar),
// whatever the Brand panel's accent: the style is the bank's, as its printed factsheet is.
export const LESHA_CHART = {
  palette: ["#163a63", "#0a76b6", "#a6a8ab", "#5c7ea6", "#8fc1e3", "#d0d3d6"],
  track: "#f2f2f2",
  barMaxWidth: 22,
  // The grey of a value written over a bar or a point.
  value: "#9aa0a6",
  font: "Tajawal, Inter, ui-sans-serif, system-ui, sans-serif",
}

// The header colour when it is dark enough to draw a line, else the title ink (a white header leaves no line).
function ruleColor(headerColor) {
  const color = headerColor || DEFAULT_TITLE_COLOR
  return readableTextOn(color) === TEXT_ON_DARK ? color : DEFAULT_TITLE_COLOR
}

const list = (value) => (Array.isArray(value) ? value : value ? [value] : [])

export function withSheetChrome(option, sheet, design = {}) {
  const chrome = SHEET_CHART_CHROME[sheet]
  if (!chrome || !option || typeof option !== "object") return option
  // Lesha sets its charts in the bank's face (Tajawal, bundled: datox/v2/sheet/lesha.css), whatever the Brand fonts.
  const font = sheet === "lesha" ? LESHA_CHART.font : designFontStack(design.bodyFont)
  const baseline = chrome.baseline === "rule" ? ruleColor(design.headerColor) : chrome.baseline
  const size = chrome.fontSize ? { fontSize: chrome.fontSize } : {}
  const label = (axisLabel) => ({ ...axisLabel, color: chrome.label, fontWeight: chrome.labelWeight, fontFamily: font, ...size })
  const category = (axisLabel) => ({ ...label(axisLabel), fontWeight: chrome.categoryWeight ?? chrome.labelWeight })
  // Tajawal's digits and capitals sit about 1.75px over the middle of its line (its tall Arabic ascent and descent):
  // a label centred on a bar or a grid line rode high (Artyom: «не выровнены цифры»). Lesha's labels drop by as much:
  // the values by an offset in their own (rotated) frame; the axis labels at the side (no offset there) hang from a
  // line twice that tall, whose middle the text takes (a top padding was not drawn on the first render).
  const sink = sheet === "lesha" ? 1.75 : 0
  const side = (axisLabel, y) => (sink && y ? { ...axisLabel, verticalAlign: "top", lineHeight: sink * 2 } : axisLabel)
  const axis = (value, y) => {
    if (!value || typeof value !== "object") return value
    if (value.type === "value")
      return {
        ...value,
        axisLine: { ...value.axisLine, show: false },
        axisTick: { ...value.axisTick, show: false },
        axisLabel: side(label(value.axisLabel), y),
        splitLine: { ...value.splitLine, lineStyle: { ...value.splitLine?.lineStyle, color: chrome.grid, type: chrome.gridType, width: 1 } },
        nameTextStyle: { ...value.nameTextStyle, color: chrome.label, fontFamily: font, ...size },
      }
    return {
      ...value,
      axisLine: { ...value.axisLine, lineStyle: { ...value.axisLine?.lineStyle, color: baseline, width: chrome.baselineWidth } },
      axisTick: { ...value.axisTick, show: false },
      axisLabel: side(category(value.axisLabel), y),
      splitLine: value.splitLine ? { ...value.splitLine, lineStyle: { ...value.splitLine.lineStyle, color: chrome.grid, type: chrome.gridType, width: 1 } } : value.splitLine,
      nameTextStyle: { ...value.nameTextStyle, color: chrome.label, fontFamily: font },
    }
  }
  // Lesha draws no grid behind bars: the light track behind each bar is the scale.
  const bars = sheet === "lesha" && list(option.series).some((series) => series?.type === "bar")
  const gridless = (value) =>
    bars && value?.type === "value" ? { ...value, splitLine: { ...value.splitLine, lineStyle: { ...value.splitLine?.lineStyle, color: "transparent" } } } : value
  const mapAxes = (value, y) => (Array.isArray(value) ? value.map((item) => gridless(axis(item, y))) : gridless(axis(value, y)))
  const legend = (value) =>
    value && value.show !== false
      ? { ...value, icon: chrome.icon, itemWidth: chrome.iconSize[0], itemHeight: chrome.iconSize[1], textStyle: { ...value.textStyle, color: chrome.legend, fontWeight: chrome.legendWeight, fontFamily: font, ...size } }
      : value
  const lesha = sheet === "lesha"
  // Bars lie along the value axis: horizontal when the value axis is x.
  const horizontal = list(option.xAxis)[0]?.type === "value" && list(option.yAxis)[0]?.type !== "value"
  // A pill, round at both ends, on a pill track (a stacked segment stays square: it meets the next one).
  const pill = [999, 999, 999, 999]
  // A value over its bar (vertical bars: written upwards, as the bank's factsheet does) or over its point, in grey.
  const valueLabel = (series) =>
    series.label?.show
      ? {
          label: {
            ...series.label,
            color: LESHA_CHART.value,
            fontSize: 10,
            fontFamily: font,
            ...(series.type === "bar" ? (horizontal ? { position: "right", distance: 6 } : { position: "top", rotate: 90, align: "left", verticalAlign: "middle", distance: 6 }) : { position: "top", distance: 8 }),
            ...(series.type === "bar" ? { offset: [0, sink] } : {}),
          },
        }
      : {}
  // A bar that reaches near the top of the scale has no room over it: its value goes inside its end, white and bold,
  // as the bank writes its tallest bars.
  const barValues = list(option.series).filter((series) => series?.type === "bar" && !series.stack).flatMap((series) => list(series.data).map((item) => Number(item?.value ?? item)))
  const tallest = Math.max(0, ...barValues.filter(Number.isFinite))
  const insideTop = (series) =>
    series.label?.show && tallest > 0
      ? {
          data: list(series.data).map((item) => {
            const value = Number(item?.value ?? item)
            if (!(Number.isFinite(value) && value >= tallest * 0.85)) return item
            const cell = item !== null && typeof item === "object" ? item : { value: item }
            return { ...cell, label: { ...cell.label, position: horizontal ? "insideRight" : "insideTop", color: "#fff", fontWeight: 700, ...(horizontal ? {} : { rotate: 90, align: "right", verticalAlign: "middle" }) } }
          }),
        }
      : {}
  // A bar's value inside its track, past the bar's end (over a vertical bar, beside a horizontal one), and inside the
  // bar itself when the track has no room left (insideTop), as the bank's factsheet writes them (Artyom: «внутри цифры
  // как у них в пдф»). The values of one chart carry the same decimals (5 → 5.00 beside 4.87), so they read alike.
  const valued = (series) => lesha && series?.type === "bar" && !series.stack && !!series.label?.show
  const written = (series, item) => {
    const value = item !== null && typeof item === "object" ? item.value : item
    return String(typeof series.label.formatter === "function" ? series.label.formatter({ value, data: item }) : (value ?? ""))
  }
  const texts = list(option.series).filter(valued).flatMap((series) => list(series.data).map((item) => written(series, item)))
  const figure = /^([-−]?[\d,]+)(?:\.(\d+))?(%?)$/
  const decimals = texts.length && texts.every((text) => figure.test(text)) ? Math.max(...texts.map((text) => (figure.exec(text)[2] || "").length)) : 0
  const padded = (text) => {
    const match = decimals ? figure.exec(text) : null
    return match ? `${match[1]}.${(match[2] || "").padEnd(decimals, "0")}${match[3]}` : text
  }
  const barLabel = (series) => {
    const label = valueLabel(series).label
    return label && decimals && valued(series) ? { label: { ...label, formatter: (params) => padded(written(series, params.data ?? params.value)) } } : label ? { label } : {}
  }
  // Each series in the bank's colour by its place (navy first): the renderer sets a series' colour from the Brand
  // accent's palette, so the option's own palette alone would not reach it. A colour set on one bar or point stays.
  const leshaSeries = (series, index) => {
    if (!series || typeof series !== "object") return series
    const color = LESHA_CHART.palette[index % LESHA_CHART.palette.length]
    if (series.type === "bar")
      return { ...series, ...barLabel(series), ...insideTop(series), barMaxWidth: series.barMaxWidth ?? LESHA_CHART.barMaxWidth, itemStyle: { ...series.itemStyle, color, borderRadius: series.stack ? 0 : pill }, showBackground: !series.stack, backgroundStyle: { color: LESHA_CHART.track, borderRadius: pill } }
    if (series.type === "line") {
      const points = Array.isArray(series.data) ? series.data.length : 0
      return { ...series, ...valueLabel(series), lineStyle: { width: 2, ...series.lineStyle, color }, itemStyle: { ...series.itemStyle, color }, ...(series.areaStyle ? { areaStyle: { ...series.areaStyle, color, opacity: 0.08 } } : {}), ...(points && points <= 24 ? { showSymbol: true, symbol: "circle", symbolSize: 7 } : {}) }
    }
    return series
  }
  // The legend under the plot, each name in its series' colour; the plot takes back the room the top legend had.
  const shownLegend = list(option.legend)[0]
  const moveLegend = lesha && shownLegend && shownLegend.show !== false && shownLegend.top !== undefined && !list(option.series).some((series) => series?.type === "pie")
  const named = list(option.series).filter((series) => series?.name)
  const leshaLegend = (value) =>
    moveLegend
      ? { ...legend(value), top: undefined, bottom: 0, data: named.map((series) => ({ name: series.name, textStyle: { color: LESHA_CHART.palette[list(option.series).indexOf(series) % LESHA_CHART.palette.length] } })) }
      : legend(value)
  // Values written over the top points or bars keep the room over the plot that the legend gave up.
  const labelled = list(option.series).some((series) => series?.label?.show)
  const grid = (value) => (moveLegend && value && typeof value === "object" ? { ...value, top: Math.max(labelled ? 24 : 14, (Number(value.top) || 36) - 22), bottom: (Number(value.bottom) || 6) + 26 } : value)
  return {
    ...option,
    ...(lesha ? { color: LESHA_CHART.palette } : {}),
    textStyle: { ...option.textStyle, fontFamily: font },
    ...(option.xAxis ? { xAxis: mapAxes(option.xAxis, false) } : {}),
    ...(option.yAxis ? { yAxis: mapAxes(option.yAxis, true) } : {}),
    ...(option.legend ? { legend: Array.isArray(option.legend) ? option.legend.map(lesha ? leshaLegend : legend) : (lesha ? leshaLegend : legend)(option.legend) } : {}),
    ...(moveLegend && option.grid ? { grid: Array.isArray(option.grid) ? option.grid.map(grid) : grid(option.grid) } : {}),
    ...(list(option.series).some((series) => series?.type === "pie") || lesha
      ? {
          series: list(option.series).map((series, index) => {
            const next = series?.type === "pie" ? { ...series, label: { ...series.label, color: chrome.legend, fontFamily: font } } : series
            return lesha ? leshaSeries(next, index) : next
          }),
        }
      : {}),
  }
}
